<!--
  로그인 (Cover) — Pineapple 톤
  ────────────────────────────────────────
  admin-4.jsx 로그인 화면 스타일:
  · 좌측 브랜드 패널 (Pine 캐릭터, 파인애플 로고, 통계)
  · 우측 로그인 폼 (이메일/비밀번호 + OAuth)
  · 풀스크린 분할 (route meta.layout='empty')
-->
<template>
  <div class="p-login">
    <!-- 좌측 브랜드 -->
    <aside class="p-login__brand">
      <div class="p-login__brand-inner">
        <!-- 로고 (favicon) -->
        <div class="p-login__logo">
          <img class="p-login__logo-img" src="/favicon.png" alt="Pineapple" />
          <div>
            <div class="p-login__brand-title">pineapple</div>
            <div class="p-login__brand-sub">INSTRUCTOR</div>
          </div>
        </div>

        <!-- 파인 (Pine) 캐릭터 — characters-a.jsx 정확한 정의 (expr='happy') -->
        <div class="p-login__hero">
          <svg viewBox="0 0 200 240" width="240" height="288" fill="none" aria-hidden="true">
            <!-- Leaves -->
            <g>
              <path d="M100 25 Q88 50 92 85 Q100 70 108 85 Q112 50 100 25 Z" fill="#7BB073" />
              <path d="M75 45 Q70 75 88 92 Q85 70 92 55 Q82 45 75 45 Z" fill="#4F8A5C" />
              <path d="M125 45 Q130 75 112 92 Q115 70 108 55 Q118 45 125 45 Z" fill="#4F8A5C" />
              <path d="M62 70 Q66 88 86 95 Q78 82 80 72 Q70 65 62 70 Z" fill="#7BB073" />
              <path d="M138 70 Q134 88 114 95 Q122 82 120 72 Q130 65 138 70 Z" fill="#7BB073" />
            </g>
            <!-- Body -->
            <path d="M100 90 C50 90 42 130 42 165 C42 205 65 220 100 220 C135 220 158 205 158 165 C158 130 150 90 100 90 Z" fill="#FFC93C" stroke="#C98A0A" stroke-width="2.5" />
            <!-- Quilt pattern -->
            <g opacity="0.35" stroke="#E5A91A" stroke-width="1.5" fill="none">
              <path d="M60 130 L80 120 L100 130 L120 120 L140 130" />
              <path d="M60 130 L80 140 L100 130 L120 140 L140 130" />
              <path d="M60 160 L80 150 L100 160 L120 150 L140 160" />
              <path d="M60 160 L80 170 L100 160 L120 170 L140 160" />
              <path d="M60 190 L80 180 L100 190 L120 180 L140 190" />
            </g>
            <!-- Seed dots -->
            <g fill="#E5A91A" opacity="0.55">
              <circle cx="80" cy="130" r="1.5" />
              <circle cx="120" cy="130" r="1.5" />
              <circle cx="100" cy="160" r="1.5" />
              <circle cx="80" cy="190" r="1.5" />
              <circle cx="120" cy="190" r="1.5" />
            </g>
            <!-- Face (expr='happy', blush=true): smile eyes + curved mouth + cheeks -->
            <g fill="#F5B5A8" opacity="0.7">
              <ellipse cx="86" cy="159" rx="6" ry="3.5" />
              <ellipse cx="114" cy="159" rx="6" ry="3.5" />
            </g>
            <g fill="none" stroke="#1F1A14" stroke-width="3.2" stroke-linecap="round">
              <path d="M83 151 Q88 145 93 151" />
              <path d="M107 151 Q112 145 117 151" />
            </g>
            <path d="M94 161 Q100 167 106 161" fill="none" stroke="#1F1A14" stroke-width="2.6" stroke-linecap="round" />
          </svg>
          <div class="p-login__hero-bubble">강사님, 환영합니다!<br/>오늘도 좋은 수업 되세요 ✨</div>
        </div>
      </div>
    </aside>

    <!-- 우측 폼 -->
    <main class="p-login__form-side">
      <div class="p-login__form-wrap">
        <div class="p-login__header">
          <div class="p-login__pill">FOR {{$CONF.APP_USERTYPE}}</div>
          <h1 class="p-login__title">강사 로그인</h1>
          <p class="p-login__desc">Enter your email and password to Sign In</p>
        </div>

        <vee-form :validation-schema="schema" class="p-login__form" @submit="onSubmit">
          <field v-slot="{ field, errors }" name="email">
            <label class="p-login__label">이메일</label>
            <input
              v-bind="field"
              type="email"
              name="email"
              placeholder="admin@pineapple.kr"
              class="p-input"
              :class="{ 'is-invalid': errors.length > 0 }"
            />
            <error-message name="email" class="p-login__err" />
          </field>

          <field v-slot="{ field, errors }" name="password">
            <label class="p-login__label">비밀번호</label>
            <input
              v-bind="field"
              type="password"
              name="password"
              placeholder="••••••••"
              class="p-input"
              :class="{ 'is-invalid': errors.length > 0 }"
            />
            <error-message name="password" class="p-login__err" />
          </field>

          <a-btn type="submit" kind="primary" class="p-login__submit">로그인</a-btn>
        </vee-form>

        <div class="p-login__divider"><span>또는</span></div>

        <o-auth-buttons :is-login="true" @success="handleOAuthSuccess" @error="handleOAuthError" />

        <div class="p-login__links">
          <span class="p-login__links-txt">계정이 없으신가요?</span>
          <router-link class="p-login__link" :to="{ name: 'Signup Cover' }">회원가입</router-link>
        </div>

        <div class="p-login__footer">
          <span>파인애플 백오피스</span>
          <span class="p-mono">v{{ $CONF.APP_VERSION || '1.0' }}</span>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import OAuthButtons from '@/components/auth/OAuthButtons.vue';
import { Field, Form as VeeForm, ErrorMessage } from 'vee-validate';
import * as yup from 'yup';
import ABtn from '@/components/Pineapple/ABtn.vue';

export default {
  name: 'SigninCover',
  components: { OAuthButtons, Field, VeeForm, ErrorMessage, ABtn },
  data() {
    return {
      schema: yup.object({
        email: yup.string().email('이메일 형식이 올바르지 않습니다').required('이메일을 입력해주세요'),
        password: yup.string().min(6, '비밀번호는 최소 6자 이상이어야 합니다').required('비밀번호를 입력해주세요'),
      }),
    };
  },
  methods: {
    onSubmit(values) {
      this.$store.dispatch(this.$CONF.ACT_LOGIN, values);
    },
    handleOAuthSuccess() {
      console.log('OAuth login successful');
    },
    handleOAuthError(e) {
      console.error('OAuth login failed:', e);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_pineapple.scss';

.p-login {
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1fr 1fr;
  background: $p-bg;
  font-family: $p-font-body;
  color: $p-ink;

  @media (max-width: 900px) { grid-template-columns: 1fr; }
}

.p-mono { font-family: $p-font-mono; font-size: 11px; color: $p-ink-soft; }

.p-login__brand {
  background: $p-sidebar;
  color: $p-sidebar-tx;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px;
  background-image:
    radial-gradient(circle at 20% 30%, rgba(255,201,60,0.10) 0%, transparent 50%),
    radial-gradient(circle at 80% 70%, rgba(255,201,60,0.06) 0%, transparent 50%);

  @media (max-width: 900px) { display: none; }
}
.p-login__brand-inner {
  max-width: 480px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 48px;
  align-items: center;
  text-align: center;
}

.p-login__logo {
  display: flex;
  align-items: center;
  gap: 14px;
}
.p-login__logo-img {
  width: 56px;
  height: 56px;
  object-fit: contain;
  filter: drop-shadow(0 4px 12px rgba(255, 201, 60, 0.4));
}
.p-login__brand-title {
  font-size: 22px; font-weight: 900;
  letter-spacing: -0.02em; line-height: 1.1;
  color: $p-sidebar-tx;
}
.p-login__brand-sub {
  font-size: 11px; color: $p-sidebar-mid;
  font-weight: 700; letter-spacing: 0.18em;
  text-transform: uppercase; margin-top: 4px;
}

.p-login__hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.p-login__hero-bubble {
  background: $p-point;
  color: $p-ink;
  padding: 12px 18px;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.5;
  position: relative;
  box-shadow: 0 8px 24px -8px rgba(255, 201, 60, 0.5);
  text-align: center;

  &::before {
    content: '';
    position: absolute;
    top: -8px; left: 50%;
    transform: translateX(-50%);
    border: 8px solid transparent;
    border-bottom-color: $p-point;
    border-top: 0;
  }
}

/* 우측 폼 */
.p-login__form-side {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px;
}
.p-login__form-wrap {
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.p-login__header {
  text-align: left;
}
.p-login__pill {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  background: $p-point-soft;
  color: $p-point-deep;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  margin-bottom: 12px;
}
.p-login__title {
  font-size: 26px;
  font-weight: 900;
  letter-spacing: -0.025em;
  color: $p-ink;
  margin: 0 0 8px;
  line-height: 1.2;
}
.p-login__desc {
  font-size: 13px;
  color: $p-ink-mid;
  margin: 0;
}

.p-login__form {
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.p-login__label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: $p-ink;
  margin-bottom: 6px;
}
.p-input {
  width: 100%;
  height: 44px;
  padding: 0 14px;
  border: 1px solid $p-line;
  border-radius: 8px;
  background: $p-panel;
  font-family: $p-font-body;
  font-size: 13px;
  color: $p-ink;
  outline: none;

  &:focus { border-color: $p-point; box-shadow: 0 0 0 3px rgba(255, 201, 60, 0.2); }
  &.is-invalid { border-color: $p-err; }
  &::placeholder { color: $p-ink-soft; }
}
.p-login__err {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: $p-err;
}
.p-login__submit {
  height: 44px;
  font-size: 13px;
  width: 100%;
  margin-top: 6px;
  justify-content: center;
}

.p-login__divider {
  text-align: center;
  position: relative;
  font-size: 11px;
  color: $p-ink-soft;
  font-weight: 700;
  letter-spacing: 0.1em;

  &::before {
    content: '';
    position: absolute;
    left: 0; right: 0; top: 50%;
    height: 1px; background: $p-line;
    z-index: 0;
  }
  span {
    background: $p-bg;
    padding: 0 14px;
    position: relative;
    z-index: 1;
  }
}

.p-login__links {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  font-size: 12px;
}
.p-login__link {
  color: $p-ink-mid;
  font-weight: 700;
  text-decoration: none;

  &:hover { color: $p-ink; text-decoration: underline; }
}
.p-login__links-txt { color: $p-ink-soft; }

.p-login__footer {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: $p-ink-soft;
  padding-top: 16px;
  border-top: 1px solid $p-line;
}
</style>
